/**
 * What the player holds while a student is at a quiz, and how it is handed over.
 *
 * The sitting belongs to the CLIENT. play-service keeps it in Redis until the quiz is submitted, so
 * the web and the app share one state and a half-played quiz stays invisible to the teacher, the
 * reports and the ETL. This file is the shape of what travels between them, and the two rules that
 * decide what a hand-over says.
 */

/** One question as the server hands it over: frozen, so every student sees the same numbers. */
export interface QuizQuestion {
  type: 'AdaptivePractice' | 'HOTS';
  question_id: string;
  lo_id?: string;
  review_obj?: any;
  points: number;
  /** Where it sits in the quiz as the teacher built it - what the server files an answer under. */
  index: number;
}

/** What the student has done with one question, as the server is told it. */
export interface QuizAnswer {
  index: number;
  /**
   * Answered, or seen and not answered. There is no skipping: leaving a question and moving on is
   * the same act, and the server files both the same way. (It still ACCEPTS 'skipped' from an older
   * client, and collapses it into the same row.)
   */
  status: 'answered' | 'unanswered';
  answer: any[];
  isCorrect: boolean;
  /**
   * TOTAL seconds on that question across every visit, not this visit's.
   *
   * The server keeps the larger of what it holds and what arrives, so a hand-over that is retried,
   * or one that overtakes another, can never shrink a question's time.
   */
  time_spent: number;
  /**
   * Came back from the server rather than being done here - an earlier sitting, as `fetch` handed
   * it over. Those are left OUT of a hand-over: the server already has them, and `fetch` withholds
   * verdicts mid-attempt, so handing one back would say `isCorrect: false` about an answer that was
   * right. Harmless, because the server keeps its own verdict for an answer that has not changed -
   * but it makes the request a confusing thing to read.
   *
   * Marked on the ones that came from the server rather than on the ones done here, deliberately.
   * The two fail in opposite directions: forget to mark something done here and it is sent anyway,
   * which costs nothing; forget the other way round and it is never sent, which is how a question
   * seen and left stopped being recorded and came back in the next sitting.
   */
  fromServer?: boolean;
}

/**
 * What a hand-over carries: this sitting's doing, with the marker stripped.
 *
 * NOT only the questions answered - a question seen and left is recorded here as a blank, and that
 * blank is what tells the server it is used up and must not be offered again.
 */
export const sittingToSend = (held: { [index: number]: QuizAnswer }): QuizAnswer[] =>
  Object.values(held)
    .filter((a) => a.fromServer !== true)
    .map(({ fromServer, ...answer }) => answer);

/** A question shown and left without an answer still counts as seen, and says so. */
export const blankAnswer = (index: number, seconds: number): QuizAnswer => ({
  index,
  status: 'unanswered',
  answer: [],
  isCorrect: false,
  time_spent: Math.max(1, Math.round(seconds)),
});

/**
 * Which of two clocks to believe.
 *
 * The countdown runs locally, because a number redrawn from the server jumps about - 58, 60, 58 - and
 * reads as broken. But a tab in the background has its timers throttled, so the local count can
 * silently stall while the quiz's own clock runs on. Taking the SMALLER of the two means the display
 * only ever corrects downwards, which is the honest direction: the student cannot gain time by
 * hiding the tab, and never loses time they did not have.
 *
 * `null` from the server means the teacher set no limit at all - there is no clock to correct.
 */
export const agreedTimeLeft = (local: number | null, fromServer: number | null) => {
  if (fromServer === null || fromServer === undefined) return local;
  if (local === null) return fromServer;
  return Math.min(local, fromServer);
};
